import classNames from 'classnames';
import { FC, FocusEvent, MouseEvent, useEffect, useRef, useState } from 'react';

import {
  Control,
  Controller,
  DeepRequired,
  FieldErrorsImpl,
  UseFieldArrayInsert,
  UseFieldArrayRemove,
  UseFieldArrayUpdate,
  UseFormRegister,
  UseFormSetFocus,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';

import {
  AssessmentQuestionType,
  AssessmentSignatureUserInput,
  AutofillSource,
  ContactType,
} from '../../../../generated/graphql';

import {
  PlusSmIcon,
  ChevronDownIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/outline';
import { $convertToMarkdownString } from '@lexical/markdown';
import { MARKDOWN_TRANSFORMERS } from '../../../components/Editor/plugins/MarkdownTransformers';
import CloseX from '../../../svgs/CloseX';
import Trash from '../../../svgs/Trash';
import DocumentPlusIcon from '../../../svgs/DocumentPlusIcon';

import {
  isArrowDownKey,
  isArrowUpKey,
  isBackspaceKey,
  isEnterKey,
  resizeTextArea,
} from '../../../lib/form';

import IconButton from '../../../components/IconButton';
import InputGroup from '../../../components/InputGroup';
import SelectMenu from '../../../components/SelectMenu';
import Tooltip from '../../../components/Tooltip';
import CountrySelector from '../../../components/CountrySelector';
import LanguageSelector from '../../../components/LanguageSelector';
import TimezoneSelector from '../../../components/TimezoneSelector';

import FormBlock from '../FormBlock';
import { defaultQuestionSchema } from '../helpers';

import Editor from '../../../components/Editor';
import {
  isFreeTextAssessmentQuestion,
  isMultiSelectAssessmentQuestion,
  isMultipleChoiceAssessmentQuestion,
  isRichTextAssessmentQuestion,
  isSignatureAssessmentQuestion,
  isMultiSignatureAssessmentQuestion,
  isStatementAssessmentQuestion,
  isInitialsAssessmentQuestion,
  isSignatureTypeAssessmentQuestion,
  isAutofillAssessmentQuestion,
  isDatePickerAssessmentQuestion,
} from '../../../lib/assessments';
import { formatContactTypeLabel } from '../../../lib/patientContactLabels';
import {
  Question,
  QuestionForm,
  QuestionTypeItem,
  SUPPORTED_QUESTION_TYPE_OPTIONS,
} from './helpers';
import Checkbox from '../../../components/Checkbox';
import Button from '../../../components/Button';

import SignatureUserDisplay from '../../../components/SignatureUserDisplay';
import SignatureFlagName from '../../../svgs/SignatureFlagName';
import { customToast } from '../../../components/ToastAlert/customToast';
import {
  FILE_UPLOAD_EXTENSION_CONFIGS,
  ALL_FILE_UPLOAD_EXTENSIONS,
  getUniqueExtensionLabels,
} from '../../../lib/fileUtils';

const MAX_ANSWER_OPTIONS = 100;

// Component for selecting allowed file extensions
const FileExtensionsSelector: FC<{
  questionIndex: number;
  control: Control<QuestionForm>;
  value: string[];
}> = ({ questionIndex, control, value: initialValue }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <div className="mt-4">
      <Controller
        control={control}
        name={
          `questions.${questionIndex}.allowedExtensions` as AllowedExtensionsRegisterValue
        }
        defaultValue={initialValue}
        render={({ field: { onChange, value } }) => {
          const selectedExtensions = value || [];

          // Get unique groups and their extensions
          const groupedExtensions = FILE_UPLOAD_EXTENSION_CONFIGS.reduce(
            (acc, item) => {
              const groupKey = item.group || item.ext;
              if (!acc[groupKey]) {
                acc[groupKey] = {
                  label: item.label,
                  extensions: [],
                };
              }
              acc[groupKey].extensions.push(item.ext);
              return acc;
            },
            {} as Record<string, { label: string; extensions: string[] }>,
          );

          const toggleExtensionGroup = (groupKey: string) => {
            const group = groupedExtensions[groupKey];
            const allSelected = group.extensions.every((ext) =>
              selectedExtensions.includes(ext),
            );

            let newExtensions: string[];
            if (allSelected) {
              // Remove all extensions in the group
              newExtensions = selectedExtensions.filter(
                (ext) => !group.extensions.includes(ext),
              );
            } else {
              // Add all extensions in the group
              const toAdd = group.extensions.filter(
                (ext) => !selectedExtensions.includes(ext),
              );
              newExtensions = [...selectedExtensions, ...toAdd];
            }
            onChange(newExtensions);
          };

          // Count unique groups selected
          const selectedGroups = Object.keys(groupedExtensions).filter(
            (groupKey) => {
              const group = groupedExtensions[groupKey];
              return group.extensions.some((ext) =>
                selectedExtensions.includes(ext),
              );
            },
          );

          // Get unique group labels for display
          const selectedGroupLabels = new Set<string>();
          selectedExtensions.forEach((ext) => {
            Object.entries(groupedExtensions).forEach(([groupKey, group]) => {
              if (group.extensions.includes(ext)) {
                selectedGroupLabels.add(group.label);
              }
            });
          });

          const allGroupLabels = Object.values(groupedExtensions).map(
            (g) => g.label,
          );
          const uniqueAllGroupLabels = Array.from(new Set(allGroupLabels));

          const displayText =
            selectedGroups.length === 0
              ? 'Select allowed file types'
              : selectedGroups.length === Object.keys(groupedExtensions).length
              ? uniqueAllGroupLabels.join(', ')
              : `${selectedGroups.length} file type${
                  selectedGroups.length > 1 ? 's' : ''
                } selected`;

          return (
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex w-full items-center justify-between rounded-md border border-neutral-100 bg-white px-3 py-2 text-left text-caption text-neutral-150 hover:border-neutral-125 focus:outline-none focus:ring-2 focus:ring-secondary-125"
              >
                <span>{displayText}</span>
                <ChevronDownIcon
                  className={classNames(
                    'h-4 w-4 text-neutral-125 transition-transform',
                    isDropdownOpen && 'rotate-180',
                  )}
                />
              </button>
              {isDropdownOpen && (
                <div className="absolute z-10 mt-1 w-full rounded-md border border-neutral-100 bg-white shadow-lg">
                  <div className="max-h-60 overflow-y-auto p-2">
                    {Object.entries(groupedExtensions).map(
                      ([groupKey, group]) => {
                        const isGroupSelected = group.extensions.some((ext) =>
                          selectedExtensions.includes(ext),
                        );
                        return (
                          <div key={groupKey} className="py-1">
                            <Checkbox
                              id={`ext-${questionIndex}-${groupKey}`}
                              checked={isGroupSelected}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleExtensionGroup(groupKey);
                              }}
                              labelContent={
                                <span className="ml-2 text-caption">
                                  {group.label}
                                </span>
                              }
                            />
                          </div>
                        );
                      },
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        }}
      />
    </div>
  );
};

const MAX_ANSWER_OPTION_LENGTH = 500;
const MAX_ANSWER_OPTION_WARNING_LENGTH = 450;

type QuestionRegisterValue = `questions.${number}.question`;
type QuestionTypeRegisterValue = `questions.${number}.questionType`;
type IsRequiredRegisterValue = `questions.${number}.isRequired`;
type AllowedExtensionsRegisterValue = `questions.${number}.allowedExtensions`;

type AnswerOptionRegisterValue = `questions.${number}.answerOptions.${number}`;

const Bullet = () => (
  <div className="mr-2 mt-3 h-2.5 w-2.5 rounded-full border-2 border-secondary-125 bg-white" />
);

const Square = () => (
  <div className="mr-2 mt-3 h-3 w-3 rounded-sm border-2 border-secondary-125 bg-white" />
);

const getLabels = (
  question: Question,
  isSignatureTypeQuestion: boolean,
): [string, string, string] => {
  if (isStatementAssessmentQuestion(question)) {
    return ['Statement', 'Enter a statement...', 'Please enter a statement'];
  } else if (isSignatureTypeQuestion) {
    return ['Prompt', 'Enter a prompt...', 'Please enter a prompt'];
  } else {
    return ['Question', 'Enter a question...', 'Please enter a question'];
  }
};

const getQuestionInputId = (index: number) => `questionInput_${index}`;

const WINDOW_OFFSET = 145;

const QuestionContainer: FC<{
  completableByProviderOnly?: boolean;
  question: Question;
  questionIndex: number;
  isLastQuestion: boolean;
  errors: FieldErrorsImpl<DeepRequired<QuestionForm>>;
  control: Control<QuestionForm, object>;
  register: UseFormRegister<QuestionForm>;
  insert: UseFieldArrayInsert<QuestionForm, 'questions'>;
  remove: UseFieldArrayRemove;
  update: UseFieldArrayUpdate<QuestionForm, 'questions'>;
  setValue: UseFormSetValue<QuestionForm>;
  setFocus: UseFormSetFocus<QuestionForm>;
  watchAssessmentSignatureUsers: AssessmentSignatureUserInput[];
  openSignatureSettingsModal: () => void;
  watch: UseFormWatch<QuestionForm>;
  setCurrentQuestionIndex: React.Dispatch<React.SetStateAction<number>>;
  currentQuestionIndex: number;
  fromServiceBuilder: boolean;
}> = ({
  question,
  questionIndex,
  isLastQuestion,
  errors,
  control,
  register,
  setValue,
  insert,
  remove,
  update,
  setFocus,
  watchAssessmentSignatureUsers,
  openSignatureSettingsModal,
  watch,
  setCurrentQuestionIndex,
  currentQuestionIndex,
  fromServiceBuilder,
  completableByProviderOnly = false,
}) => {
  const questionRegisterValue: QuestionRegisterValue = `questions.${questionIndex}.question`;
  const questionTypeRegisterValue: QuestionTypeRegisterValue = `questions.${questionIndex}.questionType`;
  const isRequiredRegisterValue: IsRequiredRegisterValue = `questions.${questionIndex}.isRequired`;

  const useableQuestionRef = useRef<HTMLTextAreaElement | null>(null);

  // resizes QuestionTextAreas on initial load
  useEffect(() => {
    resizeTextArea(useableQuestionRef);
  }, [useableQuestionRef]);

  const scrollToQuestion = (
    targetElement: HTMLElement | null,
    offset: number,
    clickY = 0,
  ) => {
    if (targetElement) {
      const containerRect = targetElement.getBoundingClientRect();
      const containerHeight = containerRect.height;
      const clickedInBottomHalf =
        clickY > containerRect.top + containerHeight / 2;

      let targetScrollTop;
      if (clickedInBottomHalf) {
        targetScrollTop =
          containerRect.bottom + window.scrollY - window.innerHeight + offset;
      } else {
        targetScrollTop = containerRect.top + window.scrollY - offset;
      }

      window.scrollTo({
        top: targetScrollTop,
        behavior: 'smooth',
      });
    }
  };

  const scrollQuestionContainerToPosition = (
    event:
      | MouseEvent<HTMLDivElement, globalThis.MouseEvent>
      | FocusEvent<
          HTMLTextAreaElement | HTMLInputElement | HTMLButtonElement,
          Element
        >,
  ) => {
    const clickY = 'clientY' in event ? event.clientY : 0;
    scrollToQuestion(questionContainerRef.current, WINDOW_OFFSET, clickY);
    event.stopPropagation();
  };

  const isFirstQuestion = questionIndex === 0;

  const questionContainerRef = useRef<HTMLDivElement>(null);
  const questionInputRef = useRef<HTMLDivElement>(null);

  const selectAnswerOption = (
    answerOptionRegisterValue: AnswerOptionRegisterValue,
  ) => {
    setTimeout(() => {
      setFocus(answerOptionRegisterValue);
    }, 0);
  };

  const isInitialsQuestion = isInitialsAssessmentQuestion(question);
  const isSignatureQuestion = isSignatureAssessmentQuestion(question);
  const isMultiSignatureQuestion = isMultiSignatureAssessmentQuestion(question);
  const isProviderSignatureQuestion =
    question.questionType === AssessmentQuestionType.ProviderSignature;
  const isSignatureTypeQuestion = isSignatureTypeAssessmentQuestion(question);
  const isAutofillQuestion = isAutofillAssessmentQuestion(question);
  const isDatePickerQuestion = isDatePickerAssessmentQuestion(question);
  const isPatientContactQuestion =
    question.questionType === AssessmentQuestionType.PatientContact;

  // Watch autofillSource to update preview
  const watchedAutofillSource = watch(
    `questions.${questionIndex}.autofillSource`,
  ) as AutofillSource | undefined;

  const [label, placeholder, errorMessage] = getLabels(
    question,
    isSignatureTypeQuestion,
  );

  const watchAssessmentUserOrderIndices = watch(
    `questions.${questionIndex}.assessmentUserOrderIndices`,
  );
  const watchedPatientContactType = watch(
    `questions.${questionIndex}.contactType`,
  ) as ContactType | undefined;

  const addAssessmentSignatureUserToQuestion = (
    assessmentSignatureUser: AssessmentSignatureUserInput,
  ) => {
    const updatedIndices = [
      ...watchAssessmentUserOrderIndices,
      assessmentSignatureUser.orderIndex,
    ].sort((a, b) => a - b);
    setValue(
      `questions.${questionIndex}.assessmentUserOrderIndices`,
      updatedIndices,
    );
  };

  const removeSignatureUserFromQuestion = (index: number) => {
    const updatedIndices = watchAssessmentUserOrderIndices.filter(
      (orderIndex) => orderIndex !== index,
    );
    setValue(
      `questions.${questionIndex}.assessmentUserOrderIndices`,
      updatedIndices,
    );
  };

  // Generalized handler for inserting a question and scrolling
  const handleInsertQuestionBelow = (
    e: React.MouseEvent<HTMLButtonElement>,
    newQuestion: any,
  ) => {
    const nextQuestionIndex = questionIndex + 1;
    insert(nextQuestionIndex, newQuestion);

    setTimeout(() => {
      const nextQuestionInputId = getQuestionInputId(nextQuestionIndex);
      const nextQuestionInputElement =
        document.getElementById(nextQuestionInputId);
      scrollToQuestion(nextQuestionInputElement, WINDOW_OFFSET + 100);
    }, 50);

    e.preventDefault();
  };

  // Handler for adding a new question of the same type
  const handleAddQuestionBelow = (e: React.MouseEvent<HTMLButtonElement>) => {
    handleInsertQuestionBelow(e, {
      ...defaultQuestionSchema,
      // Default to adding another question of the same type
      questionType: question.questionType,
      ...(isMultiSignatureQuestion && {
        assessmentUserOrderIndices: watchAssessmentSignatureUsers.map(
          (_, index) => index,
        ),
      }),
      ...(question.questionType === AssessmentQuestionType.FileUpload && {
        allowedExtensions: Array.from(ALL_FILE_UPLOAD_EXTENSIONS),
      }),
      ...(question.questionType === AssessmentQuestionType.PatientContact && {
        contactType: question.contactType ?? ContactType.EmergencyContact,
      }),
    });
  };

  const handleDuplicatePatientContactBelow = (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    handleInsertQuestionBelow(e, {
      ...defaultQuestionSchema,
      questionType: AssessmentQuestionType.PatientContact,
      question: question.question,
      isRequired: question.isRequired,
      contactType: question.contactType ?? ContactType.EmergencyContact,
    });
  };

  // Handler for duplicating a multiple choice/select question with its options
  const handleDuplicateQuestionBelow = (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    handleInsertQuestionBelow(e, {
      ...defaultQuestionSchema,
      questionType: question.questionType,
      answerOptions: question.answerOptions
        ? JSON.parse(JSON.stringify(question.answerOptions))
        : [],
    });
  };

  return (
    <div className="relative" ref={questionContainerRef}>
      <FormBlock
        onClick={(e) => {
          if (questionIndex !== currentQuestionIndex) {
            scrollQuestionContainerToPosition(e);
          }
          setCurrentQuestionIndex(questionIndex);
        }}
      >
        <div className="mb-3 flex flex-row items-center justify-between">
          <div className="text-small-caption font-medium uppercase text-secondary-100">
            {label}
          </div>
          <div className="flex flex-row items-center">
            {!isSignatureTypeQuestion &&
              !isStatementAssessmentQuestion(question) && (
                <Controller
                  control={control}
                  name={isRequiredRegisterValue}
                  render={({ field: { onChange, value } }) => (
                    <Checkbox
                      id={isRequiredRegisterValue}
                      checked={value}
                      onChange={onChange}
                      className="mr-4"
                      labelContent="Required"
                    />
                  )}
                />
              )}
            <Controller
              control={control}
              name={questionTypeRegisterValue}
              defaultValue={AssessmentQuestionType.MultipleChoice}
              rules={{ required: true }}
              render={({ field }) => {
                const { onChange, value } = field;

                return (
                  <SelectMenu
                    fieldValue={value}
                    onChange={(value) => {
                      if (
                        fromServiceBuilder &&
                        value === AssessmentQuestionType.MultiSignature
                      ) {
                        customToast.warning(
                          "Collaborative signature isn't available for intake forms.",
                        );
                        return;
                      }
                      // Prevent client signature questions in provider-only forms
                      if (completableByProviderOnly) {
                        const clientSignatureTypes: AssessmentQuestionType[] = [
                          AssessmentQuestionType.Initials,
                          AssessmentQuestionType.Signature,
                          AssessmentQuestionType.MultiSignature,
                        ];
                        if (
                          clientSignatureTypes.includes(
                            value as AssessmentQuestionType,
                          )
                        ) {
                          customToast.error(
                            'Provider-only forms cannot have client signature questions. Use provider signature instead.',
                          );
                          return;
                        }
                        if (value === AssessmentQuestionType.PatientContact) {
                          customToast.error(
                            'Client Contact questions are not available on practitioner-only forms.',
                          );
                          return;
                        }
                      }
                      // Prevent provider signature in non-provider-only forms
                      if (
                        !completableByProviderOnly &&
                        value === AssessmentQuestionType.ProviderSignature
                      ) {
                        customToast.error(
                          'Practitioner signature questions are only available for Practitioner forms.',
                        );
                        return;
                      }
                      onChange(value);
                    }}
                    fieldOptions={SUPPORTED_QUESTION_TYPE_OPTIONS.filter(
                      (option) => {
                        const optionType = option as AssessmentQuestionType;

                        // Filter question types based on provider-only status
                        if (completableByProviderOnly) {
                          // Hide client signature types when provider-only is enabled
                          const clientSignatureTypes: AssessmentQuestionType[] =
                            [
                              AssessmentQuestionType.Initials,
                              AssessmentQuestionType.Signature,
                              AssessmentQuestionType.MultiSignature,
                            ];
                          return (
                            !clientSignatureTypes.includes(optionType) &&
                            optionType !== AssessmentQuestionType.PatientContact
                          );
                        } else {
                          // Hide provider signature when provider-only is NOT enabled
                          return (
                            optionType !==
                            AssessmentQuestionType.ProviderSignature
                          );
                        }
                      },
                    )}
                    SelectOptionComponent={QuestionTypeItem}
                    buttonClassName="max-h-[40px] min-w-[210px]"
                    placeholder="Select a form or assessment"
                    label="Select question type"
                    hideLabel
                  />
                );
              }}
            />
          </div>
        </div>
        <div
          id={getQuestionInputId(questionIndex)}
          ref={questionInputRef}
          className="flex w-full flex-row items-start justify-between rounded-lg border border-neutral-75 p-4"
        >
          <Controller
            control={control}
            name={questionRegisterValue}
            rules={{
              required: {
                value: true,
                message: errorMessage,
              },
            }}
            render={({ field }) => {
              const { onChange, value } = field;
              return (
                <Editor
                  placeholder={placeholder}
                  className="text-neutral-150"
                  containerClassName="w-full"
                  initialContentMarkdown={value}
                  onChange={(editorState) => {
                    editorState.read(() => {
                      onChange(
                        $convertToMarkdownString(
                          MARKDOWN_TRANSFORMERS,
                          undefined,
                          true,
                        ),
                      );
                    });
                  }}
                  onError={(error: Error) => {
                    console.error(error);
                  }}
                />
              );
            }}
          />
        </div>
        <div className="mt-2 flex items-center text-small-caption text-red-100">
          {errors.questions?.[questionIndex]?.question?.message}
        </div>
        {question.questionType === AssessmentQuestionType.FileUpload && (
          <FileExtensionsSelector
            questionIndex={questionIndex}
            control={control}
            value={question.allowedExtensions || []}
          />
        )}
        {isAutofillAssessmentQuestion(question) && (
          <>
            {/* Help Text with Tooltip */}
            <div className="mt-4">
              <Tooltip
                placement="bottom-start"
                content={
                  <div className="space-y-2">
                    <p>
                      Autofill fields automatically populate with data from the
                      client's profile when they open the form.
                    </p>
                    <p>
                      Select an autofill source below to choose which profile
                      field should populate this question.
                    </p>
                  </div>
                }
              >
                <span className="flex cursor-help items-center gap-1.5 text-small-caption text-neutral-125">
                  <QuestionMarkCircleIcon className="h-4 w-4 text-neutral-125" />
                  How does autofill work?
                </span>
              </Tooltip>
            </div>
            {/* Autofill Source Dropdown */}
            <div className="mt-4">
              <Controller
                control={control}
                name={`questions.${questionIndex}.autofillSource`}
                render={({ field }) => {
                  const { onChange, value } = field;
                  const autofillSourceOptions = Object.values(
                    AutofillSource,
                  ).map((source) => {
                    let label = source
                      .replace(/([A-Z])/g, ' $1')
                      .replace(/^./, (str) => str.toUpperCase())
                      .trim();
                    // Fix "Participant Id" -> "Participant ID"
                    if (label === 'Participant Id') {
                      label = 'Participant ID';
                    }
                    return {
                      label,
                      value: source,
                    };
                  });
                  return (
                    <SelectMenu
                      fieldValue={value}
                      onChange={onChange}
                      fieldOptions={autofillSourceOptions}
                      buttonClassName="max-h-[40px] min-w-[210px]"
                      placeholder="Select autofill source"
                      label="Autofill source"
                    />
                  );
                }}
              />
            </div>
          </>
        )}
        {isDatePickerAssessmentQuestion(question) && (
          <div className="mt-4">
            <Controller
              control={control}
              name={`questions.${questionIndex}.defaultToToday`}
              render={({ field }) => {
                const { onChange, value } = field;
                return (
                  <Checkbox
                    id={`question-${questionIndex}-default-to-today`}
                    checked={value || false}
                    onChange={(e) => onChange(e.target.checked)}
                    labelContent={
                      <div className="ml-2 text-caption">
                        Default to today's date
                      </div>
                    }
                  />
                );
              }}
            />
          </div>
        )}
        {isPatientContactQuestion && (
          <div className="mt-4 space-y-3">
            <div className="text-small-caption font-medium uppercase text-secondary-100">
              Contact category
            </div>
            <Controller
              control={control}
              name={`questions.${questionIndex}.contactType`}
              render={({ field }) => {
                const { onChange, value } = field;
                return (
                  <SelectMenu
                    fieldValue={value ?? ContactType.EmergencyContact}
                    onChange={onChange}
                    fieldOptions={Object.values(ContactType).map((ct) => ({
                      label: formatContactTypeLabel(ct),
                      value: ct,
                    }))}
                    buttonClassName="max-h-[40px] min-w-[210px]"
                    placeholder="Select contact type"
                    label="Contact type"
                  />
                );
              }}
            />
          </div>
        )}
        <div className="mt-6 flex flex-row items-center justify-start pb-2">
          {(isFreeTextAssessmentQuestion(question) ||
            isRichTextAssessmentQuestion(question)) && (
            <div
              className={classNames(
                'w-full rounded-lg border border-neutral-75 bg-neutral-25 p-4 text-caption text-neutral-110',
                {
                  'pb-20': isRichTextAssessmentQuestion(question),
                },
              )}
            >
              Response goes here...
            </div>
          )}
          {question.questionType === AssessmentQuestionType.FileUpload && (
            <div className="w-full rounded-md bg-neutral-25 px-4 py-3 text-caption text-neutral-125">
              {(() => {
                const allowedExtensions = question.allowedExtensions || [];
                const extensionsToShow =
                  allowedExtensions.length > 0
                    ? allowedExtensions
                    : Array.from(ALL_FILE_UPLOAD_EXTENSIONS);
                const extensionLabels =
                  getUniqueExtensionLabels(extensionsToShow).join(', ');
                return `Allowed file types: ${extensionLabels} (max 10MB)`;
              })()}
            </div>
          )}
          {isInitialsQuestion && (
            <div className="flex flex-col items-start justify-start">
              <div className="mb-3 text-caption font-medium">
                Click the box to add your initials
              </div>
              <InputGroup
                IconLeft={SignatureFlagName}
                containerClassName="mb-3 w-[120px]"
                className="rounded-lg pl-12"
                disabled
              />
            </div>
          )}
          {isSignatureQuestion && (
            <div className="flex flex-col items-start justify-start">
              <div className="mb-3 text-caption font-medium">
                Write your name to sign electronically
              </div>
              <InputGroup
                IconLeft={SignatureFlagName}
                containerClassName="mb-3 w-[398px]"
                className="rounded-lg pl-12"
                disabled
                placeholder={`Client signs name here`}
              />
            </div>
          )}
          {isProviderSignatureQuestion && (
            <div className="flex flex-col items-start justify-start">
              <div className="mb-3 text-caption font-medium">
                Provider will sign electronically
              </div>
              <InputGroup
                IconLeft={SignatureFlagName}
                containerClassName="mb-3 w-[398px]"
                className="rounded-lg pl-12"
                disabled
                placeholder={`Provider signs name here`}
              />
            </div>
          )}
          {isMultiSignatureQuestion && (
            <div className="flex w-full flex-col items-start justify-start">
              {watchAssessmentSignatureUsers.length < 2 ? (
                <Button
                  title="Add form signatures"
                  iconPosition="right"
                  size="small"
                  theme="secondary"
                  IconComponent={PlusSmIcon}
                  iconClassName="text-secondary-100"
                  onClick={openSignatureSettingsModal}
                />
              ) : (
                <div className="flex w-full flex-col items-start justify-start">
                  <div className="mb-4 text-body font-medium text-neutral-125">
                    Choose signers for this section
                  </div>

                  {watchAssessmentSignatureUsers?.map(
                    (assessmentSignatureUser, index) => {
                      const isSignatureUserInQuestion =
                        watchAssessmentUserOrderIndices.includes(index);

                      return assessmentSignatureUser ? (
                        <div
                          key={index}
                          className="mb-4 flex w-auto cursor-pointer select-none flex-row items-center justify-start rounded-2xl border border-neutral-50"
                          onClick={() => {
                            if (isSignatureUserInQuestion) {
                              removeSignatureUserFromQuestion(index);
                            } else {
                              addAssessmentSignatureUserToQuestion(
                                assessmentSignatureUser,
                              );
                            }
                          }}
                        >
                          <SignatureUserDisplay
                            assessmentSignatureUser={assessmentSignatureUser}
                            disabled={!isSignatureUserInQuestion}
                          />
                          <Checkbox
                            id={`question_${questionIndex}_multi_ signature_user_${index}`}
                            checked={isSignatureUserInQuestion}
                            aria-label={
                              isSignatureUserInQuestion
                                ? 'Remove form signer'
                                : 'Add form signer'
                            }
                            className="ml-10 mr-4"
                          />
                        </div>
                      ) : null;
                    },
                  )}
                </div>
              )}
            </div>
          )}
          {isAutofillQuestion && (
            <div className="flex flex-col items-start justify-start">
              <div className="mb-3 text-caption font-medium">
                Autofill field preview
              </div>
              {(() => {
                // Render preview based on selected autofill source
                const autofillSource = watchedAutofillSource;

                // Country selector
                if (autofillSource === AutofillSource.AddressCountry) {
                  return (
                    <div className="mb-3 w-full md:w-[398px]">
                      <CountrySelector
                        value="United States"
                        onChange={() => undefined}
                        disabled
                        placeholder="Select a country..."
                      />
                    </div>
                  );
                }

                // Language selector
                if (autofillSource === AutofillSource.PreferredLanguage) {
                  return (
                    <div className="mb-3 w-full md:w-[398px]">
                      <LanguageSelector
                        value="English"
                        onChange={() => undefined}
                        disabled
                        placeholder="Select a language..."
                      />
                    </div>
                  );
                }

                // Timezone selector
                if (autofillSource === AutofillSource.Timezone) {
                  return (
                    <div className="mb-3 w-full md:w-[398px]">
                      <TimezoneSelector
                        value=""
                        onChange={() => undefined}
                        disabled
                      />
                    </div>
                  );
                }

                // Date of Birth - date input
                if (autofillSource === AutofillSource.DateOfBirth) {
                  return (
                    <InputGroup
                      type="date"
                      containerClassName="mb-3 w-full md:w-[398px]"
                      inputSize="extra-small"
                      disabled
                      placeholder="mm/dd/yyyy"
                    />
                  );
                }

                // Practitioner Name - dropdown
                if (autofillSource === AutofillSource.PractitionerName) {
                  return (
                    <div className="mb-3 w-full md:w-[398px]">
                      <SelectMenu
                        fieldValue=""
                        onChange={() => undefined}
                        fieldOptions={[
                          {
                            label: 'Example Practitioner',
                            value: 'Example Practitioner',
                          },
                        ]}
                        label="Practitioner Name"
                        hideLabel
                        placeholder="Example Practitioner"
                        disabled
                      />
                    </div>
                  );
                }

                // Participant ID - read-only text input
                if (autofillSource === AutofillSource.ParticipantId) {
                  return (
                    <InputGroup
                      containerClassName="mb-3 w-full md:w-[398px]"
                      inputSize="extra-small"
                      disabled
                      readOnly
                      placeholder="55WS37BI"
                      className="cursor-not-allowed bg-neutral-50"
                    />
                  );
                }

                // Default text input with source-specific placeholders
                const getPlaceholder = () => {
                  switch (autofillSource) {
                    case AutofillSource.FullName:
                      return 'John Doe';
                    case AutofillSource.Email:
                      return 'john.doe@example.com';
                    case AutofillSource.PhoneNumber:
                      return '(555) 123-4567';
                    case AutofillSource.AddressStreet:
                      return '123 Main Street';
                    case AutofillSource.AddressCity:
                      return 'New York';
                    case AutofillSource.AddressState:
                      return 'NY';
                    case AutofillSource.AddressPostalCode:
                      return '10001';
                    case AutofillSource.PractitionerRole:
                      return 'Chief Medical Officer';
                    default:
                      return 'Value will be autofilled from selected source';
                  }
                };

                return (
                  <InputGroup
                    containerClassName="mb-3 w-full md:w-[398px]"
                    inputSize="extra-small"
                    disabled
                    placeholder={getPlaceholder()}
                  />
                );
              })()}
            </div>
          )}
          {isDatePickerQuestion && (
            <div className="flex flex-col items-start justify-start">
              <div className="mb-3 text-caption font-medium">
                Date picker preview
              </div>
              <InputGroup
                type="date"
                containerClassName="mb-3 w-full md:w-[398px]"
                inputSize="extra-small"
                disabled
              />
            </div>
          )}
          {isPatientContactQuestion && (
            <div className="w-full rounded-lg border border-neutral-75 bg-neutral-25 p-4 text-caption text-neutral-110">
              Client selects an existing Contact from their account (
              {formatContactTypeLabel(
                watchedPatientContactType ??
                  question.contactType ??
                  ContactType.EmergencyContact,
              )}
              ) or adds a new one while completing the form.
            </div>
          )}

          {(isMultipleChoiceAssessmentQuestion(question) ||
            isMultiSelectAssessmentQuestion(question)) && (
            <div className="flex w-full flex-col items-start justify-start">
              <div className="mb-2 text-small-caption font-medium uppercase text-secondary-100">
                Options
              </div>
              {question.answerOptions?.map(
                (answerOption, answerOptionIndex) => {
                  const answerOptionRegisterValue: AnswerOptionRegisterValue = `questions.${questionIndex}.answerOptions.${answerOptionIndex}`;
                  const previousAnswerOptionRegisterValue: AnswerOptionRegisterValue = `questions.${questionIndex}.answerOptions.${
                    answerOptionIndex - 1
                  }`;
                  const nextAnswerOptionRegisterValue: AnswerOptionRegisterValue = `questions.${questionIndex}.answerOptions.${
                    answerOptionIndex + 1
                  }`;

                  const isFirstAnswerOption = answerOptionIndex === 0;
                  const isFirstOrSecondAnswerOption = answerOptionIndex < 2;

                  const lastAnswerOptionIndex =
                    question.answerOptions.length - 1;
                  const isLastAnswerOption =
                    answerOptionIndex === lastAnswerOptionIndex;

                  const hasAvailableOptions =
                    question.answerOptions.length < MAX_ANSWER_OPTIONS;
                  const hasAnswerOptionInput = Boolean(answerOption);

                  const isEligibleToAddOptions =
                    hasAvailableOptions && isLastAnswerOption;

                  const answerOptionRegister = register(
                    answerOptionRegisterValue,
                    {
                      required: {
                        value: true,
                        message: !isFirstOrSecondAnswerOption
                          ? 'Please write text or remove this answer option'
                          : !isFirstQuestion
                          ? 'Please enter at least 2 answer options or remove the question'
                          : 'Please enter at least 2 answer options',
                      },
                      onBlur: () => {
                        if (answerOption) {
                          setValue(
                            answerOptionRegisterValue,
                            answerOption
                              .trim()
                              .slice(0, MAX_ANSWER_OPTION_LENGTH),
                          );
                        } else if (
                          isLastAnswerOption &&
                          !isFirstOrSecondAnswerOption
                        ) {
                          update(questionIndex, {
                            ...question,
                            answerOptions: question.answerOptions.slice(
                              0,
                              lastAnswerOptionIndex,
                            ),
                          });
                        }
                      },
                    },
                  );

                  const addAnswerOption = () => {
                    update(questionIndex, {
                      ...question,
                      answerOptions: [...question.answerOptions, ''],
                    });
                  };

                  const deleteAnswerOption = () => {
                    if (!(isFirstAnswerOption && !hasAnswerOptionInput)) {
                      const answerOptionsToSplice = question.answerOptions;
                      answerOptionsToSplice.splice(answerOptionIndex, 1);

                      update(questionIndex, {
                        ...question,
                        answerOptions: answerOptionsToSplice,
                      });

                      selectAnswerOption(previousAnswerOptionRegisterValue);
                    }
                  };

                  const onAnswerOptionInputKeyDown = (
                    event: React.KeyboardEvent<HTMLInputElement>,
                  ) => {
                    const keyDown = event.key;

                    if (isEnterKey(keyDown)) {
                      if (isEligibleToAddOptions) {
                        addAnswerOption();
                      }
                      selectAnswerOption(nextAnswerOptionRegisterValue);
                      event.preventDefault();
                    }

                    if (isBackspaceKey(keyDown) && !hasAnswerOptionInput) {
                      if (!isFirstOrSecondAnswerOption) {
                        deleteAnswerOption();
                      } else if (!isFirstAnswerOption) {
                        selectAnswerOption(previousAnswerOptionRegisterValue);
                      }
                      event.preventDefault();
                    }

                    if (
                      isArrowDownKey(keyDown) &&
                      answerOptionIndex < lastAnswerOptionIndex
                    ) {
                      selectAnswerOption(nextAnswerOptionRegisterValue);
                      event.preventDefault();
                    }

                    if (isArrowUpKey(keyDown) && !isFirstAnswerOption) {
                      setFocus(previousAnswerOptionRegisterValue);
                      event.preventDefault();
                    }
                  };

                  const showCharacterCounter =
                    answerOption?.length > MAX_ANSWER_OPTION_WARNING_LENGTH;

                  return (
                    <div
                      key={answerOptionIndex}
                      className="flex w-full flex-col items-start justify-start"
                    >
                      <div className="mb-2 flex w-full flex-row items-start justify-start">
                        {isMultiSelectAssessmentQuestion(question) ? (
                          <Square />
                        ) : (
                          <Bullet />
                        )}
                        <InputGroup
                          label={answerOptionRegisterValue}
                          labelHidden
                          autoFocus={false}
                          backgroundHidden
                          onKeyDown={onAnswerOptionInputKeyDown}
                          errorMessage={
                            errors.questions?.[questionIndex]?.answerOptions?.[
                              answerOptionIndex
                            ]?.message
                          }
                          inputSize="extra-small"
                          placeholder={`Option ${answerOptionIndex + 1}`}
                          characterCounter={showCharacterCounter}
                          maxLength={MAX_ANSWER_OPTION_LENGTH}
                          value={answerOption}
                          currentLengthValue={answerOption?.length ?? 0}
                          maxLengthValue={MAX_ANSWER_OPTION_LENGTH}
                          containerClassName="w-full"
                          className={classNames(
                            'rounded-none border-0 border-b-2',
                            showCharacterCounter && 'group-focus-within:pr-20',
                          )}
                          type="text"
                          {...answerOptionRegister}
                        />
                        <IconButton
                          aria-label="Delete option"
                          IconComponent={CloseX}
                          className="mt-1.5 hover:bg-neutral-50"
                          disabled={
                            isFirstOrSecondAnswerOption &&
                            question.answerOptions.length < 3
                          }
                          iconClassName="h-3 w-3 pl-0.5 mt-0.5"
                          onClick={() => deleteAnswerOption()}
                        />
                      </div>
                      {isEligibleToAddOptions && (
                        <div className="flex w-full flex-row items-start justify-start">
                          {isMultiSelectAssessmentQuestion(question) ? (
                            <Square />
                          ) : (
                            <Bullet />
                          )}
                          <button
                            tabIndex={-1}
                            className={classNames(
                              'mt-1 flex h-[30px] w-full flex-row items-center justify-start',
                              'border-b border-b-neutral-25 px-3 py-1 pb-2 text-body text-neutral-100',
                            )}
                            onClick={(e) => {
                              if (isEligibleToAddOptions) {
                                addAnswerOption();
                              }
                              selectAnswerOption(nextAnswerOptionRegisterValue);
                              e.preventDefault();
                            }}
                          >
                            Add option
                          </button>
                        </div>
                      )}
                    </div>
                  );
                },
              )}
            </div>
          )}
        </div>
        <div className="mt-4 flex w-full flex-row items-center justify-end border-t border-neutral-50 pt-2">
          <IconButton
            aria-label="Remove question"
            IconComponent={Trash}
            disabled={isFirstQuestion && isLastQuestion}
            iconClassName="h-5 w-5"
            className="hover:bg-neutral-50"
            onClick={() => remove(questionIndex)}
          />
        </div>
      </FormBlock>
      <button
        className={classNames(
          'absolute -right-16 top-0 flex h-10 w-10 flex-col items-center justify-center',
          'rounded-full bg-white shadow-100',
          'hover:cursor-pointer hover:bg-neutral-25',
          'focus:outline-none focus:ring-2 focus:ring-secondary-50',
        )}
        onClick={handleAddQuestionBelow}
      >
        <PlusSmIcon
          className="h-6 w-6 text-secondary-100"
          aria-label="Add a new question below"
        />
      </button>
      {/* Duplicate question button for MultipleChoice, MultiSelect, or Client Contact */}
      {(question.questionType === 'MultipleChoice' ||
        question.questionType === 'MultiSelect' ||
        question.questionType === AssessmentQuestionType.PatientContact) && (
        <button
          className={classNames(
            'absolute -right-16 top-12 flex h-10 w-10 flex-col items-center justify-center',
            'rounded-full bg-white shadow-100',
            'hover:cursor-pointer hover:bg-neutral-25',
            'focus:outline-none focus:ring-2 focus:ring-secondary-50',
          )}
          onClick={
            question.questionType === AssessmentQuestionType.PatientContact
              ? handleDuplicatePatientContactBelow
              : handleDuplicateQuestionBelow
          }
        >
          <DocumentPlusIcon
            ariaLabel="Duplicate question below"
            className="h-6 w-6 text-secondary-100"
          />
        </button>
      )}
    </div>
  );
};

export default QuestionContainer;
